import { itemQuantity, listItemName, newItem, newList, type ListDocument, type ListItem, type ListsState } from "../domain/lists";
import { enumeratedListItems, validListCommand, type ListCommand } from "../../backend/src/list-command.js";
import { isVoiceCancelCommand } from "./voice-commands";
export { enumeratedListItems } from "../../backend/src/list-command.js";

export const normalizedItemName = (name: string) => name.toLocaleLowerCase("ru-RU").replace(/ё/g, "е").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
export function duplicateOf(items: ListItem[], item: ListItem): ListItem | undefined {
  return items.find(other => other.id !== item.id && normalizedItemName(other.name) === normalizedItemName(item.name));
}
export function mergeItems(existing: ListItem, incoming: ListItem): ListItem {
  if (existing.unit !== incoming.unit || (existing.amount === null) !== (incoming.amount === null)) throw new Error("Разные единицы или не указано количество. Уточните позиции перед объединением.");
  return { ...existing, status: "needed", amount: existing.amount === null ? null : existing.amount + incoming.amount!, note: [...new Set([existing.note, incoming.note].filter(Boolean))].join("; "), sourceDish: [...new Set([existing.sourceDish, incoming.sourceDish].filter(Boolean))].join(", ") };
}
export function rememberItems(doc: ListDocument, items: ListItem[]): ListDocument {
  const history = [...items, ...doc.history].filter((item, index, all) => all.findIndex(other => normalizedItemName(other.name) === normalizedItemName(item.name) && other.unit === item.unit && other.note === item.note) === index).slice(0, 300);
  return { ...doc, history };
}
export function removeListItem(doc: ListDocument, itemId: string): ListDocument {
  const item = doc.items.find(item => item.id === itemId);
  return { ...rememberItems(doc, item ? [item] : []), items: doc.items.filter(item => item.id !== itemId) };
}
export function copyList(doc: ListDocument, kind: ListDocument["kind"], name: string): ListDocument {
  return { ...newList(name, kind), grouped: doc.grouped, completed: doc.completed, items: doc.items.map(item => ({ ...item, id: crypto.randomUUID(), status: "needed" })) };
}
export function listClipboardText(doc: ListDocument): string {
  const items = doc.items.map(item => `${item.status === "done" ? "☑" : "☐"} ${listItemName(item.name)}${itemQuantity(item) ? ` — ${itemQuantity(item)}` : ""}`);
  return [listItemName(doc.name), ...items].join("\n");
}
export function dishIngredients(dish: ListDocument): ListItem[] {
  return dish.items.map(item => ({ ...item, id: crypto.randomUUID(), status: "needed", sourceDish: dish.name }));
}
export interface ListMove { documentId: string; itemId: string }
export function commitListPreview(state: ListsState, preview: ListDocument, move: ListMove | null): ListsState {
  const updated = { ...preview, updatedAt: new Date().toISOString() };
  if (move && (move.documentId === preview.id || !state.documents.some(doc => doc.id === move.documentId && doc.items.some(item => item.id === move.itemId)))) throw new Error("Исходная позиция изменилась. Повторите перенос.");
  const documents = state.documents.map(doc => doc.id === preview.id ? updated : doc.id === move?.documentId ? { ...removeListItem(doc, move.itemId), updatedAt: updated.updatedAt } : doc);
  if (!documents.some(doc => doc.id === preview.id)) documents.push(updated);
  return { ...state, documents };
}
export function visibleListItems(doc: ListDocument): ListItem[] {
  const items = doc.items.filter(item => doc.completed !== "hide" || item.status !== "done");
  return items.sort((a, b) => (doc.grouped ? a.group.localeCompare(b.group, "ru") : 0));
}
export function moveItemInList(doc: ListDocument, itemId: string, direction: -1 | 1): ListDocument {
  const item = doc.items.find(item => item.id === itemId);
  if (!item) return doc;
  const peers = doc.items.filter(other => (!doc.grouped || other.group === item.group) && (doc.completed !== "bottom" || (other.status === "done") === (item.status === "done")));
  const neighbor = peers[peers.findIndex(other => other.id === itemId) + direction];
  if (!neighbor) return doc;
  const items = [...doc.items], index = items.indexOf(item), other = items.indexOf(neighbor);
  [items[index], items[other]] = [items[other]!, items[index]!];
  return { ...doc, items };
}
/** Replace the saved snapshot with the visible draft before every correction. */
export function withListDraft(state: ListsState, draft: ListDocument | null): ListsState {
  if (!draft) return state;
  return { ...state, documents: state.documents.some(doc => doc.id === draft.id) ? state.documents.map(doc => doc.id === draft.id ? draft : doc) : [...state.documents, draft] };
}
export function applyListCommand(state: ListsState, selectedId: string | null, input: unknown): ListDocument {
  if (!validListCommand(input)) throw new Error("Не удалось разобрать команду. Повторите её.");
  const command = input as ListCommand;
  if (command.question) throw new Error(command.question);
  if (command.newListName && command.listId) throw new Error("Не удалось определить список. Назовите его ещё раз.");
  const target = command.newListName ? newList(command.newListName) : state.documents.find(doc => doc.id === (command.listId ?? selectedId));
  if (!target || target.archivedAt) throw new Error("Выберите список или скажите: «Создай список Покупки».");
  let draft = structuredClone(target);
  for (const op of command.operations) {
    if (op.type === "merge") {
      const sources = draft.items.filter(item => op.itemIds.includes(item.id));
      if (sources.length !== op.itemIds.length) throw new Error("Не удалось найти все позиции для объединения. Список не изменён.");
      const first = sources[0]!;
      const total = op.amountMode === "sum" ? sources.slice(1).reduce((sum, item) => mergeItems(sum, item), first) : null;
      const merged = { ...first, ...op.item, name: listItemName(op.item.name), ...(total ? { amount: total.amount, unit: total.unit } : {}), status: "needed" as const, sourceDish: [...new Set(sources.map(item => item.sourceDish).filter(Boolean))].join(", ") };
      draft = { ...rememberItems(draft, sources), items: draft.items.flatMap(item => item.id === first.id ? [merged] : op.itemIds.includes(item.id) ? [] : [item]) };
    } else if (op.type === "addDish") {
      const dish = state.documents.find(doc => doc.id === op.dishId && !doc.archivedAt && doc.kind === "dish");
      if (!dish || draft.kind !== "list") throw new Error("Выберите сохранённое блюдо и список покупок.");
      draft.items.push(...dishIngredients(dish));
    } else if (op.type === "add") {
      const item = { ...newItem(op.item.name), ...op.item, name: listItemName(op.item.name) };
      const matches = draft.items.filter(other => normalizedItemName(other.name) === normalizedItemName(item.name));
      if (op.amountMode === "increment" && matches.length > 1) throw new Error("В списке несколько таких позиций. Уточните нужную.");
      const existing = matches[0];
      if (op.amountMode === "increment" && existing) draft.items = draft.items.map(other => other.id === existing.id ? mergeItems(existing, item) : other);
      else draft.items.push(item);
    } else {
      const item = draft.items.find(item => item.id === op.itemId);
      if (!item) throw new Error("Позиция не найдена. Список не изменён.");
      if (op.type === "delete") draft = removeListItem(draft, item.id);
      else if (op.type === "status") {
        draft.items = draft.items.map(other => other.id === item.id ? { ...other, status: op.status } : other);
        if (op.status === "done") draft = rememberItems(draft, [item]);
      } else {
        const named = { ...op.item, name: listItemName(op.item.name) };
        const updated = op.amountMode === "increment" ? mergeItems(item, { ...item, ...named }) : { ...item, ...named };
        draft.items = draft.items.map(other => other.id === item.id ? updated : other);
      }
    }
  }
  return draft;
}
export async function parseListCommand(text: string, state: ListsState, selectedId: string | null, signal: AbortSignal, currentItemId: string | null = null): Promise<ListDocument> {
  if (isVoiceCancelCommand(text)) return state.documents.find(doc => doc.id === selectedId) ?? (() => { throw new Error("Список не выбран"); })();
  const root = import.meta.env.DEV ? "/api" : "https://api.yumnote.ru";
  const context = (doc: ListDocument) => ({ id: doc.id, name: doc.name, kind: doc.kind, items: doc.items.map(({ id, name, amount, unit, note, group, status }) => ({ id, name, amount, unit, note, group, status })) });
  const selected = state.documents.find(doc => doc.id === selectedId);
  const current = selected ? context(selected) : null;
  const request = () => fetch(`${root}/v1/lists/command`, { method: "POST", signal: AbortSignal.any([signal, AbortSignal.timeout(45_000)]), headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, current, currentItemId: current?.items.some(item => item.id === currentItemId) ? currentItemId : null, lists: state.documents.filter(doc => !doc.archivedAt && doc.kind === "list").map(context), dishes: state.documents.filter(doc => !doc.archivedAt && doc.kind === "dish").map(({ id, name }) => ({ id, name })) }) });
  const applyFallback = () => {
    const fallback = enumeratedListItems(text);
    const target = state.documents.find(doc => doc.id === selectedId && !doc.archivedAt);
    return target && fallback.length >= 2 ? { ...target, items: [...target.items, ...fallback.map(name => newItem(name))] } : null;
  };
  let response: Response;
  try {
    response = await request();
    if (!response.ok && response.status >= 500) response = await request();
  } catch (error) {
    if (signal.aborted) throw error;
    const fallback = applyFallback();
    if (fallback) return fallback;
    throw new Error("Не удалось обработать команду. Повторите ещё раз. Список не изменён.");
  }
  if (!response.ok) {
    const fallback = applyFallback();
    if (fallback) return fallback;
    throw new Error("Не удалось обработать команду. Повторите ещё раз. Список не изменён.");
  }
  const command: unknown = await response.json();
  if (currentItemId && validListCommand(command) && !command.question && command.operations.length === 1 && command.operations[0]?.type === "add" && !/(?:^|\s)(?:добавь|добавить|допиши|внеси|запиши)(?:\s|$)/iu.test(text)) {
    const operation = command.operations[0];
    return applyListCommand(state, selectedId, { ...command, operations: [{ ...operation, type: "update", itemId: currentItemId }] });
  }
  return applyListCommand(state, selectedId, command);
}
